import {
  getAccommodationQueryOptions,
  getAccommodationWithFulfillmentsQueryOptions,
} from "@/hooks/accommodations";
import { getReservationClientsOptions } from "@/hooks/reservation-clients";
import { getReservationDaysQueryOptions } from "@/hooks/reservation-days";
import {
  getAllReservationServicesQueryOptions,
  getAllReservationServicesWithFulfillmentsQueryOptions,
  getReservationServicesQueryOptions,
} from "@/hooks/reservation-services";
import {
  getReservationQueryOptions,
  getReservationsQueryOptions,
} from "@/hooks/reservations";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import {
  Accommodation,
  AccommodationDtoSchema,
  accommodationFromDto,
  accommodationToDto,
  createAccommodationToDto,
  CreateReservation,
  createReservationClientToDto,
  createReservationDayToDto,
  createReservationServiceToDto,
  createReservationToDto,
  Reservation,
  ReservationClient,
  reservationClientToDto,
  ReservationDayDtoSchema,
  reservationDayToDto,
  ReservationDtoSchema,
  reservationFromDto,
  reservationServiceToDto,
  ReservationServiceDtoSchema,
  reservationToDto,
  ReservationService,
  reservationServiceFromDto,
} from "@/models";
import { LazyOperationEntity } from "@/models/commons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Day } from "../-types";

interface UpsertReservationInstance {
  reservation: LazyOperationEntity<CreateReservation>;
  clients: Array<LazyOperationEntity<ReservationClient>>;
  schedule: Day[];
}

export function useUpsertReservation(
  rid: number,
  type: "create" | "update",
  opts?: { onSuccess?: (id: string) => void },
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({
      clients,
      reservation,
      schedule,
    }: UpsertReservationInstance) => {
      const id = await upsertReservation(rid, reservation);
      await upsertClients(id, clients);
      await upsertDays(id, schedule);
      return id;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({
        queryKey: getReservationsQueryOptions.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: getReservationQueryOptions(id.toString()).queryKey,
      });

      queryClient.invalidateQueries({
        queryKey: getAccommodationQueryOptions(id.toString()).queryKey,
      });

      queryClient.invalidateQueries({
        queryKey: getAccommodationWithFulfillmentsQueryOptions(id.toString())
          .queryKey,
      });

      queryClient.invalidateQueries({
        queryKey: getReservationDaysQueryOptions(id.toString()).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: getReservationServicesQueryOptions(id.toString()).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: getAllReservationServicesWithFulfillmentsQueryOptions(
          id.toString(),
        ).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: getAllReservationServicesQueryOptions.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: getReservationClientsOptions(id.toString()).queryKey,
      });
      toast({
        title:
          type === "create"
            ? "toast.reservationCreated"
            : "toast.reservationUpdated",
        variant: "success",
      });
      opts?.onSuccess?.(id.toString());
    },
    onError: () => {
      toast({
        title:
          type === "create"
            ? "toast.createReservationError"
            : "toast.updateReservationError",
        variant: "destructive",
      });
    },
  });
}

async function upsertReservation(
  reservationId: number,
  reservation: LazyOperationEntity<CreateReservation>,
): Promise<number> {
  if (
    reservation.operation === null ||
    reservation.operation === "noop" ||
    reservation.operation === "delete"
  ) {
    return reservationId;
  }
  let result: Reservation;

  if (reservation.operation === "create") {
    const dto = createReservationToDto(reservation.value);
    result = reservationFromDto(
      await api.post("/api/reservations", dto, ReservationDtoSchema),
    );
  } else {
    const dto = reservationToDto({
      ...reservation.value,
      id: reservationId.toString(),
    });
    result = reservationFromDto(
      await api.put("/api/reservations", dto, ReservationDtoSchema),
    );
  }

  return Number.parseInt(result.id);
}

async function upsertClients(
  reservationId: number,
  clients: Array<LazyOperationEntity<ReservationClient>>,
): Promise<void> {
  const promises: Promise<void>[] = [];
  for (const client of clients) {
    if (client.operation === null || client.operation === "noop") {
      continue;
    }

    switch (client.operation) {
      case "create": {
        const dto = createReservationClientToDto({
          ...client.value,
          reservationId,
        });
        promises.push(api.post("/api/reservation-clients", dto, z.any()));
        break;
      }
      case "update": {
        const dto = reservationClientToDto({ ...client.value, reservationId });
        promises.push(api.put("/api/reservation-clients", dto, z.any()));
        break;
      }
      case "delete": {
        const dto = reservationClientToDto({ ...client.value, reservationId });
        promises.push(api.deleteEntity("/api/reservation-clients", dto));
        break;
      }
    }
  }
  await Promise.all(promises);
}

async function upsertDays(reservationId: number, schedule: Day[]) {
  const promises: Promise<void>[] = [];

  for (const day of schedule) {
    switch (day.reservationDay.operation) {
      case "delete": {
        promises.push(
          api.delete("/api/reservation-days/$id", void 0, {
            pathReplace: { $id: day.reservationDay.value.id },
          }),
        );
        break;
      }
      case "create": {
        promises.push(
          api
            .post(
              "/api/reservation-days",
              createReservationDayToDto({
                ...day.reservationDay.value,
                reservationId,
              }),
              ReservationDayDtoSchema,
            )
            .then(() => {}),
        );
        break;
      }
      case "update": {
        promises.push(
          api
            .put(
              "/api/reservation-days",
              reservationDayToDto({
                ...day.reservationDay.value,
                reservationId,
              }),
              ReservationDayDtoSchema,
            )
            .then(() => {}),
        );
        break;
      }
    }

    for (const service of day.services) {
      if (service.operation === "noop" || service.operation === null) continue;
      switch (service.operation) {
        case "create": {
          promises.push(
            api
              .post(
                "/api/reservation-services",
                createReservationServiceToDto({
                  ...service.value,
                  reservationId,
                  reservationClients: []
                } as ReservationService),

                ReservationServiceDtoSchema,
              )
              .then(reservationServiceFromDto)
              .then(async (newService) => {
                await api.put(
                  "/api/reservation-services",
                  reservationServiceToDto({
                    ...newService,
                    reservationClients: service.value.reservationClients,
                  }),
                  z.any(),
                );
              }),
          );
          break;
        }
        case "update": {
          promises.push(
            api
              .put(
                "/api/reservation-services",
                reservationServiceToDto({
                  ...service.value,
                  reservationId,
                  reservationClients: service.value.reservationClients
                }),
                z.any(),
              )
              .then(() => {}),
          );
          break;
        }
        case "delete": {
          promises.push(
            api
              .delete("/api/reservation-services/$id", undefined, {
                pathReplace: { $id: service.value.id },
              })
              .then(() => {}),
          );
          break;
        }
      }
    }

    for (const acc of day.accommodations) {
      if (acc.operation === null || acc.operation === "noop") continue;
      switch (acc.operation) {
        case "delete": {
          promises.push(
            api
              .delete("/api/accommodations/$id", undefined, {
                pathReplace: { $id: acc.value.id },
              })
              .then(() => {}),
          );
          break;
        }
        case "create": {
          promises.push(
            api
              .post(
                "/api/accommodations",
                createAccommodationToDto({
                  ...acc.value,
                  reservationClients: [],
                } as Accommodation),

                AccommodationDtoSchema,
              )
              .then(accommodationFromDto)
              .then(async (newAccommodation) => {
                await api.put(
                  "/api/accommodations",
                  accommodationToDto({
                    ...newAccommodation,
                    reservationClients: acc.value.reservationClients,
                  }),
                  z.any(),
                );
              }),
          );
          break;
        }
        case "update": {
          promises.push(
            api
              .put(
                "/api/accommodations",
                accommodationToDto({
                  ...acc.value,
                  reservationClients: acc.value.reservationClients,
                }),
                z.any(),
              )
              .then(() => {}),
          );
          break;
        }
      }
    }
  }

  await Promise.all(promises);
}
